import { ApiError, type ProblemBody, UnauthorizedError } from './types';
import { sendWithXhr, type Transport, type UploadProgress, type XhrLike } from './xhrTransport';

/**
 * The one HTTP client for the SPA — a thin `fetch` wrapper that is the first `src/lib` module
 * (`forge-et-fe/CLAUDE.md § Frontend Stack`: the query layer is chosen by its first real caller).
 *
 * It owns four cross-cutting concerns and nothing else:
 *
 *  1. **The session handshake.** Every request sends `credentials: 'include'` so the `SESSION`
 *     cookie rides along (server-side sessions, AD #5 — there is no bearer token). Paths resolve
 *     against a configurable base URL, so the same-origin dev proxy and the cross-site deployment
 *     (the SPA on `crm.<env>` calling `api.<env>`, AD #33) both work without baking in a
 *     same-origin-only assumption.
 *  2. **CSRF echo.** On a state-changing method it reads the CSRF cookie FRESH per request and
 *     echoes it in `X-XSRF-TOKEN`; a `GET` carries no CSRF header. The cookie's name is configurable
 *     (`VITE_CSRF_COOKIE_NAME`, defaulting to `XSRF-TOKEN`) because each deployed environment names
 *     its own — production's cookie also reaches the dev and UAT hosts (AD #33, F5-j). Reading per
 *     request means the token the backend rotates on sign-in is echoed on the very next write (F5-a AC-9)
 *     with no special-case re-read logic.
 *  3. **The global `401`.** A `401` throws `UnauthorizedError` AND invokes a registered
 *     `onUnauthorized` seam — the split keeps `src/lib` a leaf: the flip-to-unauthenticated lives in
 *     the session store, and the navigate lives in the app's route guard (F5-a Decision 7). It keys off
 *     the HTTP status, never the body `code`.
 *  4. **The global `403`.** A `403` throws `ApiError` exactly as before AND invokes a registered
 *     `onForbidden` seam, so the session can re-read `/me` — a permission may have been removed while
 *     the user is signed in (F5-k FR-19). Like the `401`, it keys off the HTTP status alone, never the
 *     body `code`, so a CSRF refusal triggers it too. The client only notifies; the session decides.
 *
 * `get` and `post` also take an optional `AbortSignal` (`RequestOptions`), so a caller can stop waiting
 * for a slow call — F12's inquiry extraction, which applies its own timeout and *Cancel*, and F11-h's
 * re-read of a lead under the same timeout. An aborted call
 * rejects with the signal's reason as `fetch` does; it is not an `ApiError` and runs no seam.
 *
 * `get` and `post` also take request headers (`RequestOptions.headers`) — F20-c's `Idempotency-Key` on an email
 * send. They are set before the client's own, so `Content-Type` and `X-XSRF-TOKEN` always keep the
 * client's values whatever a caller passes.
 *
 * `postFormWithProgress` reports upload progress — F20-h's email attachments. It sends a
 * `multipart/form-data` body through `XMLHttpRequest` (`xhrTransport.ts`), whose `upload` progress
 * event is the browser's only one — `fetch` has none (F20-h Wave 1 contract client-side fact 1). Only
 * the request itself changes: the base URL, the session cookie (as `withCredentials`), the CSRF echo
 * and its priming, the `401`/`403` seams and `ApiError` are the four concerns above, run unchanged.
 */

type FetchLike = typeof fetch;

/** Invoked on any `401`, in addition to the thrown `UnauthorizedError`. */
export type UnauthorizedHandler = () => void;

/** Invoked on any `403`, in addition to the thrown `ApiError`. */
export type ForbiddenHandler = () => void;

export interface ApiClientOptions {
    /** Origin the paths resolve against; default the `VITE_API_BASE_URL` env value, else same-origin relative. */
    readonly baseUrl?: string;
    /**
     * Name of the CSRF cookie to echo; default the `VITE_CSRF_COOKIE_NAME` env value, else `XSRF-TOKEN`.
     * Must match the backend's `et.security.csrf.cookie-name` for the environment. Empty means unset.
     */
    readonly csrfCookieName?: string;
    /** Injected in tests; defaults to the global `fetch`. */
    readonly fetch?: FetchLike;
    /** Registered at construction; the session store also registers one later via `setUnauthorizedHandler`. */
    readonly onUnauthorized?: UnauthorizedHandler;
    /** Registered at construction; the session store also registers one later via `setForbiddenHandler`. */
    readonly onForbidden?: ForbiddenHandler;
    /** Injected in tests; makes the request `postFormWithProgress` sends. Defaults to `new XMLHttpRequest()`. */
    readonly createXhr?: () => XhrLike;
}

/** Per-call options; `get` and `post` take them. */
export interface RequestOptions {
    /**
     * Aborts the call: `fetch` rejects with the signal's reason, which reaches the caller unchanged —
     * not an `ApiError`, and neither the `401` nor the `403` handler runs.
     */
    readonly signal?: AbortSignal | undefined;
    /**
     * Extra request headers, such as F20-c's `Idempotency-Key`. Set before the client's own headers, so
     * a caller's `Content-Type` or `X-XSRF-TOKEN` is replaced by the client's value.
     */
    readonly headers?: Readonly<Record<string, string>> | undefined;
}

/** `postFormWithProgress`'s options. */
export interface UploadOptions {
    /** Called as the body goes out, whenever the browser knows the total. */
    readonly onUploadProgress?: ((progress: UploadProgress) => void) | undefined;
    /** Aborts the upload, as `RequestOptions.signal` does: the signal's reason, no `ApiError`, no seam. */
    readonly signal?: AbortSignal | undefined;
}

export type { UploadProgress };

export interface ApiClient {
    /**
     * `GET` a JSON body, typed by the caller's generic (the API-boundary type, no cast). As for `post`,
     * `options.signal` aborts the call and `options.headers` adds request headers (`RequestOptions`).
     */
    get<T>(path: string, options?: RequestOptions): Promise<T>;
    /**
     * `POST` an optional JSON body; echoes `X-XSRF-TOKEN`. `T` defaults to `void` for a `204`.
     * `options.signal` aborts the call; `options.headers` adds request headers (`RequestOptions`).
     */
    post<T = void>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
    /**
     * `DELETE` a resource; echoes `X-XSRF-TOKEN` like `post`. `T` defaults to `void` because the
     * convention for a delete here is `204 No Content` (F20-a's `DELETE /mailbox/connection`).
     */
    delete<T = void>(path: string): Promise<T>;
    /**
     * `PUT` an optional JSON body — a whole-resource replacement such as F5-d's role save; echoes
     * `X-XSRF-TOKEN` like `post`. `T` defaults to `void` for a `204`.
     */
    put<T = void>(path: string, body?: unknown): Promise<T>;
    /**
     * `PATCH` an optional JSON body — a partial update such as F5-c's user edit, where an absent
     * member means "leave unchanged", or F26-b's standard edit, archive and restore; echoes
     * `X-XSRF-TOKEN` like `post`. `T` defaults to `void` for a `204` or an empty `200`.
     */
    patch<T = void>(path: string, body?: unknown): Promise<T>;
    /**
     * `POST` a `multipart/form-data` body — F26-c's workbook check and import, which send a file
     * beside text and JSON parts. The `FormData` goes to `fetch` as it is, with **no**
     * `Content-Type` header, because the browser writes the multipart boundary into that header
     * itself. Everything else — the CSRF echo and its priming, the `401`/`403` seams, `ApiError` on
     * any other refusal and the write-body rules — is the same code `post` runs.
     */
    postForm<T = void>(path: string, form: FormData): Promise<T>;
    /**
     * `postForm` with upload progress — F20-h's email attachments. The request goes through
     * `XMLHttpRequest` (`xhrTransport.ts`); everything else is the code `postForm` runs.
     */
    postFormWithProgress<T = void>(path: string, form: FormData, options?: UploadOptions): Promise<T>;
    /** Register (or clear, with `undefined`) the global `401` handler on this instance. */
    setUnauthorizedHandler(handler: UnauthorizedHandler | undefined): void;
    /** Register (or clear, with `undefined`) the global `403` handler on this instance. */
    setForbiddenHandler(handler: ForbiddenHandler | undefined): void;
}

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** A request body: JSON-serialised with its content type, or a `FormData` passed through as it is. */
type RequestBody =
    | { readonly kind: 'json'; readonly value: unknown }
    | { readonly kind: 'form'; readonly value: FormData };

function jsonBody(value: unknown): RequestBody | undefined {
    return value === undefined ? undefined : { kind: 'json', value };
}

/** The CSRF cookie's name when neither the option nor `VITE_CSRF_COOKIE_NAME` sets one — local and CI. */
const DEFAULT_CSRF_COOKIE_NAME = 'XSRF-TOKEN';

/**
 * A safe GET whose response plants the CSRF cookie, used to prime CSRF before a write when the cookie
 * is absent — the cold start before the session bootstrap has run, or the moment just after a logout
 * that cleared it. The backend emits the CSRF cookie (under the environment's configured name,
 * `XSRF-TOKEN` by default) on every response, and `/me` is the app's canonical always-available
 * session GET, so it is the priming endpoint.
 */
const CSRF_PRIME_PATH = '/api/v1/me';

/** Read one cookie's value verbatim — Spring does not URL-encode the CSRF token, so it round-trips as stored. */
function readCookie(name: string): string | undefined {
    const jar = typeof document === 'undefined' ? '' : document.cookie;
    for (const pair of jar ? jar.split('; ') : []) {
        const eq = pair.indexOf('=');
        if (eq !== -1 && pair.slice(0, eq) === name) {
            return pair.slice(eq + 1);
        }
    }
    return undefined;
}

function resolveUrl(baseUrl: string, path: string): string {
    return baseUrl ? `${baseUrl.replace(/\/$/, '')}${path}` : path;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/** The envelope's own members; every other top-level member is an extension (`ProblemBody.extensions`). */
const ENVELOPE_MEMBERS = new Set([
    'type',
    'title',
    'status',
    'detail',
    'instance',
    'code',
    'traceId',
    'errors',
]);

/** The body's top-level members outside the envelope, or `undefined` when there are none. */
function extensionsOf(value: Record<string, unknown>): Readonly<Record<string, unknown>> | undefined {
    const extensions: Record<string, unknown> = {};
    let found = false;
    for (const [key, member] of Object.entries(value)) {
        if (!ENVELOPE_MEMBERS.has(key)) {
            extensions[key] = member;
            found = true;
        }
    }
    return found ? extensions : undefined;
}

/** Narrow a parsed body to a `ProblemBody` — from `unknown`, never a cast (conformance criterion). */
function toProblem(value: unknown): ProblemBody | undefined {
    if (!isRecord(value)) {
        return undefined;
    }
    const { status, code } = value;
    if (typeof status !== 'number' || typeof code !== 'string') {
        return undefined;
    }
    const extensions = extensionsOf(value);
    return {
        type: typeof value.type === 'string' ? value.type : 'about:blank',
        title: typeof value.title === 'string' ? value.title : '',
        status,
        instance: typeof value.instance === 'string' ? value.instance : '',
        code,
        // exactOptionalPropertyTypes: add an optional member only when present, never as `undefined`.
        ...(typeof value.detail === 'string' ? { detail: value.detail } : {}),
        ...(typeof value.traceId === 'string' ? { traceId: value.traceId } : {}),
        ...(Array.isArray(value.errors) ? { errors: value.errors } : {}),
        ...(extensions === undefined ? {} : { extensions }),
    };
}

async function readProblem(response: Response): Promise<ProblemBody | undefined> {
    let parsed: unknown;
    try {
        parsed = await response.json();
    } catch {
        // A refusal with no/invalid JSON body still classifies on its HTTP status alone.
        return undefined;
    }
    return toProblem(parsed);
}

/**
 * The JSON body of a write's response, or `undefined` when it has none.
 *
 * A `204` has no body by definition. Any other success is read as text first and parsed only when
 * the text is non-empty: an empty `200` does not always say so in `Content-Length` — a chunked or
 * HTTP/2 response carries no such header — and `response.json()` throws on an empty body, which
 * turned a write that succeeded into a reported failure. The parse result flows into the caller's
 * generic as `response.json()`'s did, with no cast.
 */
async function readWriteBody<T>(response: Response): Promise<T> {
    if (response.status === 204) {
        return undefined as T;
    }
    const text = await response.text();
    if (text.trim() === '') {
        return undefined as T;
    }
    return JSON.parse(text);
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
    // Deferred so the singleton created at module load does not capture a possibly-undefined global
    // fetch at construction, and so a test's injected fetch is honoured verbatim.
    const fetchImpl: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    const baseUrl = options.baseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '';
    // `||`, not `??`: an empty value (an unset GitHub Actions variable arrives as '') means "use the
    // default", never "read a cookie with no name".
    const csrfCookieName =
        options.csrfCookieName || import.meta.env.VITE_CSRF_COOKIE_NAME || DEFAULT_CSRF_COOKIE_NAME;
    let unauthorizedHandler = options.onUnauthorized;
    let forbiddenHandler = options.onForbidden;
    const createXhr: () => XhrLike = options.createXhr ?? (() => new XMLHttpRequest());

    async function request(
        method: string,
        path: string,
        body?: RequestBody,
        signal?: AbortSignal,
        extraHeaders?: Readonly<Record<string, string>>,
        transport: Transport = fetchImpl,
    ): Promise<Response> {
        // The caller's headers first, without the two the client owns: it sets them below when they
        // apply, so a caller can neither replace them nor add one where the client sets none.
        const headers = new Headers(extraHeaders);
        headers.delete('Content-Type');
        headers.delete('X-XSRF-TOKEN');
        const init: RequestInit = { method, credentials: 'include', headers };
        if (signal !== undefined) {
            init.signal = signal;
        }

        if (body?.kind === 'json') {
            headers.set('Content-Type', 'application/json');
            init.body = JSON.stringify(body.value);
        } else if (body?.kind === 'form') {
            // No Content-Type: the browser sets `multipart/form-data` with the boundary it generates.
            init.body = body.value;
        }
        if (STATE_CHANGING.has(method)) {
            signal?.throwIfAborted();
            let token = readCookie(csrfCookieName);
            if (token === undefined) {
                // No CSRF cookie yet (cold start, or just after a logout cleared it). Prime it with a
                // safe GET so this write is never refused for a missing token — the first-click 403.
                // Raw fetch, bypassing the 401 handling below: we want only the Set-Cookie side effect.
                try {
                    await fetchImpl(resolveUrl(baseUrl, CSRF_PRIME_PATH), {
                        method: 'GET',
                        credentials: 'include',
                        ...(signal === undefined ? {} : { signal }),
                    });
                } catch {
                    // Best-effort: if priming fails (network), the write proceeds and surfaces the real error.
                }
                token = readCookie(csrfCookieName);
            }
            if (token !== undefined) {
                headers.set('X-XSRF-TOKEN', token);
            }
            // A priming GET swallows its own failure, an abort included; the write must not start.
            signal?.throwIfAborted();
        }

        // The CSRF priming above stays on `fetch`; only the request itself takes the transport.
        const response = await transport(resolveUrl(baseUrl, path), init);

        if (response.status === 401) {
            const problem = await readProblem(response);
            unauthorizedHandler?.();
            throw new UnauthorizedError(problem);
        }
        if (response.status === 403) {
            // The handler runs before the caller's promise settles, so a 403 answered to the session's
            // own /me re-read finds that re-read still in flight and joins it rather than looping.
            const problem = await readProblem(response);
            forbiddenHandler?.();
            throw new ApiError(403, problem);
        }
        if (!response.ok) {
            throw new ApiError(response.status, await readProblem(response));
        }
        return response;
    }

    return {
        async get<T>(path: string, options?: RequestOptions): Promise<T> {
            const response = await request('GET', path, undefined, options?.signal, options?.headers);
            // The caller's generic IS the API-boundary type; the parsed body flows into it with no cast.
            return response.json();
        },
        async post<T = void>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
            // An empty body has nothing to parse — the `void` default is the whole point.
            return readWriteBody<T>(
                await request('POST', path, jsonBody(body), options?.signal, options?.headers),
            );
        },
        async delete<T = void>(path: string): Promise<T> {
            // Deliberately identical in shape to `post` above, body argument aside: `DELETE` is
            // already in STATE_CHANGING, so the CSRF echo and the cold-start priming path are the
            // same code, and `readWriteBody()` already handles the `204` a delete answers with.
            return readWriteBody<T>(await request('DELETE', path));
        },
        async put<T = void>(path: string, body?: unknown): Promise<T> {
            // The same shape as `post`: `PUT` is in STATE_CHANGING, so the CSRF echo, the cold-start
            // priming and the `401`/`403` seams apply unchanged.
            return readWriteBody<T>(await request('PUT', path, jsonBody(body)));
        },
        async patch<T = void>(path: string, body?: unknown): Promise<T> {
            // The same shape as `put`: `PATCH` is in STATE_CHANGING, so the CSRF echo, the cold-start
            // priming and the `401`/`403` seams apply unchanged.
            return readWriteBody<T>(await request('PATCH', path, jsonBody(body)));
        },
        async postForm<T = void>(path: string, form: FormData): Promise<T> {
            // `POST` is in STATE_CHANGING, so the CSRF echo, the cold-start priming and the
            // `401`/`403` seams apply unchanged; only the body kind differs from `post`.
            return readWriteBody<T>(await request('POST', path, { kind: 'form', value: form }));
        },
        async postFormWithProgress<T = void>(
            path: string,
            form: FormData,
            uploadOptions: UploadOptions = {},
        ): Promise<T> {
            // The same request as `postForm`; only the transport differs, so progress can be reported.
            return readWriteBody<T>(
                await request(
                    'POST',
                    path,
                    { kind: 'form', value: form },
                    uploadOptions.signal,
                    undefined,
                    sendWithXhr(createXhr, uploadOptions.onUploadProgress),
                ),
            );
        },
        setUnauthorizedHandler(handler: UnauthorizedHandler | undefined): void {
            unauthorizedHandler = handler;
        },
        setForbiddenHandler(handler: ForbiddenHandler | undefined): void {
            forbiddenHandler = handler;
        },
    };
}

/** The configured instance the app and its features share, so one `401` handler governs every call. */
export const apiClient = createApiClient();
